import { useEffect, useState } from 'react';
import { getPromoCodeDetails } from '@/lib/api/subscriptions';
import { PromoCodeValidation } from '../types/subscription.types';
import { QUERY_KEYS, SENTRY_FEATURES, stanNotify } from '@/lib';
import { useStanQuery } from '@/queries';
import { useUser } from '@/queries/useUser';
import { SUBSCRIPTION_STORAGE_KEYS } from '../constants';

export const usePromoCode = () => {
    const { user } = useUser();
    const [promoCode, setPromoCode] = useState('');
    const [appliedCode, setAppliedCode] = useState('');
    const [validation, setValidation] = useState<PromoCodeValidation>({
        isValid: false,
    });

    // User state for promo validation
    const userHasTrialDays = (user?.subscriptions?.platform?.data?.trial_days ?? 0) > 0;
    const userHasSubscriptionHistory = user?.has_subscription_history ?? false;
    const userHasUsedPromoCodesPreviously = (user?.subscription_promos_used?.length ?? 0) > 0;

    const {
        data: details,
        isLoading,
        isError,
    } = useStanQuery({
        queryKey: [QUERY_KEYS.PROMO_CODE_DETAILS, appliedCode],
        queryFn: () => getPromoCodeDetails(appliedCode),
        enabled: !!appliedCode,
        retry: false,
        staleTime: Infinity,
        meta: {
            error: {
                feature: SENTRY_FEATURES.AUTHENTICATION,
                sentryMessage: '[AUTHENTICATION] failed-to-validate-promo-code',
                title: 'Promo Code Error',
                message: 'Failed to validate promo code. Please try again.',
            },
        },
    });

    useEffect(() => {
        if (!appliedCode) {
            setValidation({ isValid: false });
            return;
        }

        if (isLoading) return;

        if (isError) {
            setValidation({
                isValid: false,
                errorMessage: 'This promo code is invalid or expired',
            });
            stanNotify({
                type: 'error',
                title: 'Invalid discount code',
                text: 'Unable to apply discount code.',
            });
            localStorage.removeItem(SUBSCRIPTION_STORAGE_KEYS.PROMO_ID);
            localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED, 'true');
            return;
        }

        if (details?.promo_code && details.promo_code.active) {
            const coupon = details.promo_code.coupon;
            const promoMetadata = details.promo_code.metadata || {};
            const couponMetadata = coupon.metadata || {};

            const allowStacking = promoMetadata.allow_stacking || couponMetadata.allow_stacking;

            const isWinbackOnly = promoMetadata.winback_only || couponMetadata.winback_only;

            // Validation 1: Promo code stacking check
            // Users who have used promo codes before cannot use another unless allow_stacking is set
            if (userHasUsedPromoCodesPreviously && !allowStacking) {
                setValidation({
                    isValid: false,
                    errorMessage: "You've already used a discount code before and can't apply another.",
                });
                stanNotify({
                    type: 'error',
                    title: 'Uh oh!',
                    text: "You've already used a discount code before and can't apply another.",
                });
                localStorage.removeItem(SUBSCRIPTION_STORAGE_KEYS.PROMO_ID);
                localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED, 'true');
                return;
            }

            // Validation 2: Winback code check
            // Winback codes are only for returning users (no trial days AND has subscription history)
            if (isWinbackOnly && (userHasTrialDays || !userHasSubscriptionHistory)) {
                setValidation({
                    isValid: false,
                    errorMessage: 'Unable to apply discount code',
                });
                stanNotify({
                    type: 'error',
                    title: 'Invalid discount code',
                    text: 'Unable to apply discount code',
                });
                localStorage.removeItem(SUBSCRIPTION_STORAGE_KEYS.PROMO_ID);
                localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED, 'true');
                return;
            }

            const isFree = coupon.percent_off === 100;

            // Extract trial days from promo metadata (matching Vue behavior)
            const promoTrialDays = parseInt(promoMetadata.trial_days || couponMetadata.trial_days || '0', 10);

            setValidation({
                isValid: true,
                promoId: details.promo_code.id,
                discountPercent: coupon.percent_off,
                isFreeSubscription: isFree,
                trialDays: promoTrialDays > 0 ? promoTrialDays : undefined,
            });

            stanNotify({
                type: 'success',
                title: 'Success!',
                text: 'Discount code successfully applied.',
            });

            if (isFree) {
                localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED, 'false');
            } else {
                localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED, 'true');
            }

            localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.PROMO_ID, details.promo_code.id);
        } else if (details) {
            // Details loaded but invalid/inactive
            setValidation({
                isValid: false,
                errorMessage: 'This promo code is invalid or expired',
            });
            stanNotify({
                type: 'error',
                title: 'Invalid discount code',
                text: 'Unable to apply discount code.',
            });
            localStorage.removeItem(SUBSCRIPTION_STORAGE_KEYS.PROMO_ID);
            localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED, 'true');
        }
    }, [
        details,
        isLoading,
        isError,
        appliedCode,
        userHasUsedPromoCodesPreviously,
        userHasTrialDays,
        userHasSubscriptionHistory,
    ]);

    const validatePromoCode = (code: string) => {
        if (!code.trim()) {
            setValidation({ isValid: false });
            return;
        }
        setAppliedCode(code.trim());
    };

    const clearPromoCode = () => {
        setPromoCode('');
        setAppliedCode('');
        setValidation({ isValid: false });
        localStorage.removeItem(SUBSCRIPTION_STORAGE_KEYS.PROMO_ID);
        localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED, 'true');
    };

    return {
        promoCode,
        setPromoCode,
        isValidating: isLoading,
        validation,
        validatePromoCode,
        clearPromoCode,
    };
};
